/**
 * WS-NAV — custom workspace glyphs.
 *
 * Material Icons has no quadcopter, and the nearest stand-ins (a departing
 * airliner, a helicopter) name the wrong machine: Field Data is the drone
 * pilot's workspace. So this one glyph is hand-drawn — top-down quadcopter,
 * the silhouette a pilot recognises instantly.
 *
 * Drawn as strokes on a 24×24 grid to sit level with the MUI *Outlined* set
 * the other five workspaces use (1.6 stroke ≈ the weight Material outlines
 * render at 24 px). Wrapped in `SvgIcon` so `fontSize` / `sx` / `currentColor`
 * behave exactly like a real MUI icon — the switcher, the collapsed rail and
 * the Home card all size it through those props.
 */
import { SvgIcon } from "@mui/material";
import type { ReactElement } from "react";
import type { IconComponent } from "./types.js";

/**
 * Geometry tuned for the SMALL sizes, because that is where the icon actually
 * lives: 20 px in the switcher flyout and the collapsed rail, 24 px on the
 * Home card. Fatter rotors with a bigger body close up into an indistinct
 * cluster of dots below ~24 px; pushing the rotors out, shrinking the hub and
 * thinning the stroke keeps four open rings and four visible arms at 20 px.
 */
const ROTORS: readonly (readonly [number, number])[] = [
  [5.5, 5.5],
  [18.5, 5.5],
  [5.5, 18.5],
  [18.5, 18.5],
];

const ROTOR_R = 2.9;
/** Where each arm meets its rotor ring: r/√2 along the diagonal. */
const ARM_INSET = ROTOR_R / Math.SQRT2;
/** Half-width of the body square the arms spring from. */
const BODY_HALF = 1.8;
/** Slightly under the MUI outlined set's apparent weight — the open rings
 *  would otherwise fill in at 20 px. */
const STROKE = 1.5;

export const DroneIcon: IconComponent = ({ fontSize, sx }): ReactElement => (
  <SvgIcon
    viewBox="0 0 24 24"
    {...(fontSize === undefined ? {} : { fontSize })}
    {...(sx === undefined ? {} : { sx })}
  >
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Arms — from the body corners out to each rotor ring. */}
      {ROTORS.map(([cx, cy]) => {
        const dirX = cx < 12 ? -1 : 1;
        const dirY = cy < 12 ? -1 : 1;
        return (
          <line
            key={`arm-${String(cx)}-${String(cy)}`}
            x1={12 + dirX * BODY_HALF}
            y1={12 + dirY * BODY_HALF}
            x2={cx - dirX * ARM_INSET}
            y2={cy - dirY * ARM_INSET}
          />
        );
      })}
      {/* Rotors. */}
      {ROTORS.map(([cx, cy]) => (
        <circle
          key={`rotor-${String(cx)}-${String(cy)}`}
          cx={cx}
          cy={cy}
          r={ROTOR_R}
        />
      ))}
      {/* Body. */}
      <rect
        x={12 - BODY_HALF}
        y={12 - BODY_HALF}
        width={BODY_HALF * 2}
        height={BODY_HALF * 2}
        rx={0.9}
      />
    </g>
  </SvgIcon>
);
